import { ChangeDetectorRef, Component, OnInit, } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import * as _ from 'lodash';

// data, models, helpers
import { segmentAnimation } from '../base/animations';
import {
  customFieldsSegmentData,
  descriptionLabelLookup200,
  descriptionLabelLookup2000,
  descriptionLabelLookup240,
  descriptionLabelLookup80,
  customFieldsTypeFilters,
} from '../../segment-data/custom-fields-segment-data';
import { CustomField } from '../../models/custom-field.model';
import { populateModel } from 'utils/utils';
import { Option } from 'shared/form/select/option.interface';
import { DetailsControlType } from 'shared/details-page/details-control/details-control-type';

// components
import { BaseSegmentComponent } from '../base/_base-segment.component';

// services
import { ScorecardConnectorService } from 'shared/scorecard/scorecard-connector.service';
import { BrandService } from 'shared/brand/brand.service';


@Component({
  moduleId: module.id,
  selector: 'custom-fields-segment',
  templateUrl: 'custom-fields.component.html',
  styleUrls: [
    '../base/_base-segment.component.scss',
    '../base/base-attributes-table.scss',
    'custom-fields.component.scss'
  ],
  animations: [segmentAnimation],
})
export class CustomFieldsComponent extends BaseSegmentComponent implements OnInit {

  declare segmentForm: UntypedFormGroup;
  segmentName: string = 'custom-fields';
  pathToData: string = 'customFields';
  unitsData: Array<Option> = [];
  defaultTypeId: number = 1;
  selectedTypeFilter: Option = {
    'option': 0,
    'title': 'All',
  };

  get itemDetailData(): Array<any> {
    return _.get(this.itemDetail, this.pathToData);
  }

  set itemDetailData(data: Array<any>) {
    _.set(this.itemDetail, this.pathToData, data);
  }

  get formArray(): UntypedFormArray {
    return this.segmentForm.get('customFields') as UntypedFormArray;
  }

  get filteredFormArrayControls() {
    if (this.selectedTypeFilter.option === 0) {
      return (this.segmentForm.get('customFields') as UntypedFormArray).controls;
    }
    return (this.segmentForm.get('customFields') as UntypedFormArray).controls.filter(control => {
      switch (this.selectedTypeFilter.option) {
        case 1:
        case 2:
          return control.value.type_id === this.selectedTypeFilter.option;
        case 10:
          return [3, 4, 5, 6].indexOf(control.value.type_id) !== -1;
      }
    });
  }

  get filterTypeLookup(): Array<Option> {
    return customFieldsTypeFilters;
  }

  get typesByCurrentFilter(): number {
    switch (this.selectedTypeFilter.option) {
      case 0:
        return this.defaultTypeId;
      case 1:
        return 1;
      case 2:
        return 2;
      case 10:
        return [3, 4, 5, 6].indexOf(this.defaultTypeId) !== -1 ? this.defaultTypeId : 3;
      default:
        return this.defaultTypeId;
    }
  }

  constructor(
    protected ref: ChangeDetectorRef,
    protected scorecardConnectorService: ScorecardConnectorService,
    protected brandService: BrandService,
    protected fb: UntypedFormBuilder,
  ) {
    super(ref, scorecardConnectorService, brandService, fb);
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnInit() {
    super.ngOnInit();
    this.segmentForm = this.fb.group({
      customFields: this.fb.array([])
    });
    if (this.itemDetail) { this.handleAllDataUpdate(); }
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onAddCustomFieldClick() {
    const defaultTypeId = this.typesByCurrentFilter;
    const newItem = {
      type_id: defaultTypeId,
      label: '',
      value: '',
    };
    this.addFormGroupItem(newItem);
    this.formArray.markAsDirty();
    if (!this.itemDetailData) { this.itemDetailData = []; }
    this.itemDetailData.push(newItem);
    this.ref.markForCheck();
  }

  onRowRemove(index: number) {
    this.formArray.removeAt(index);
    _.pullAt(this.itemDetailData, index);
    this.formArray.markAsDirty();
    this.ref.markForCheck();
  }

  /*******************************************
  * COMPONENT PUBLIC METHODS
  *******************************************/

  public getSegmentFormData(): any {
    return _.sortBy(this.formArray.value, 'type_id');
  }

  public getCustomFieldLabel(rowTypeId: number): string {
    switch (rowTypeId) {
      case 1:
        return 'Custom Field';
      case 2:
        return 'Price Type';
      case 3:
        return 'Description 80';
      case 4:
        return 'Description 200';
      case 5:
        return 'Description 240';
      case 6:
        return 'Description 2000';
      default:
        return 'Custom Field';
    }
  }

  public getLabelControlType(rowTypeId: number): DetailsControlType {
    switch (rowTypeId) {
      case 1:
        return DetailsControlType.Input;
      case 2:
        return DetailsControlType.Input;
      case 3:
      case 4:
      case 5:
      case 6:
        return DetailsControlType.Select;
      default:
        return DetailsControlType.Input;
    }
  }

  public getTypeLookup() {
    return customFieldsSegmentData.find(s => s.field_map === 'type_id')?.lookup;
  }

  public getLabelLookup(rowTypeId: number): Array<Option> | null {
    switch (rowTypeId) {
      case 1:
        return null;
      case 2:
        return null;
      case 3:
        return descriptionLabelLookup80;
      case 4:
        return descriptionLabelLookup200;
      case 5:
        return descriptionLabelLookup240;
      case 6:
        return descriptionLabelLookup2000;
      default:
        return null;
    }
  }

  public getDescriptionMaxLength(item): number|null {
    switch (item.type_id) {
      case 1:
        return null;
      case 2:
        return null;
      case 3:
        return 80;
      case 4:
        return 200;
      case 5:
        return 240;
      case 6:
        return 2000;
      default:
        return null;
    }
  }

  public onSelectChange(value, item) {
    this.defaultTypeId = value.option;
    super.onSelectChange(value, item);
    if (this.selectedTypeFilter.option === 0) {
      return;
    }
    switch (value.option) {
      case 0:
        return;
      case 1:
      case 2:
        this.onTypeFilterChange(value);
        return;
      case 3:
      case 4:
      case 5:
      case 6:
        this.onTypeFilterChange({
          option: 10,
          title: 'Descriptions',
        });
        return;
      default:
        return;
    }
  }

  public onTypeFilterChange($event: Option) {
    this.selectedTypeFilter = $event;
    this.ref.detectChanges();
  }

  /*******************************************
  * COMPONENT PROTECTED METHODS
  *******************************************/

  protected handleAllDataUpdate() {
    super.handleAllDataUpdate();
    this.showSegment();
  }

  protected setData(itemDetail: any) {
    this.updateAttributesForm();
  }

  /*******************************************
  * COMPONENT PRIVATE METHODS
  *******************************************/

  private updateAttributesForm(): void {
    // create formArray
    this.segmentForm.setControl('customFields', this.fb.array([]));
    _.each(this.itemDetailData, (item: any) => {
      this.addFormGroupItem(item);
    });
    this.markAsPristine();
    this.ref.markForCheck();
  }

  private addFormGroupItem(itemData: any): void {
    if (!itemData.type_id) {
      itemData.type_id = 1;
    }
    const model = populateModel(CustomField, customFieldsSegmentData, itemData);
    this.formArray.push(this.fb.group(model));
  }
}
